// What a key does on a grid's selected cell while it is not being edited: one rule for the
// Worklist's grid and the Shipping schedule's, so a change here changes both. Each grid adds its
// own keys before it (the Worklist's Space, Ctrl+Z). No imports: `scripts/routing-checks.ts`
// checks it.

/** A key as the grid sees it: a `KeyboardEvent`, or the checks' stand-in. */
export interface CellKeyEvent {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

export type CellKey =
  /** To another cell: `rows` down (up when negative), `cols` right (left); `wrap`: past the row's end to the next row (Tab). */
  | { kind: "move"; rows: number; cols: number; wrap: boolean }
  /** Home / End: the row's first or last cell. */
  | { kind: "edge"; last: boolean }
  /** Enter or F2 edits; a typed character edits starting with it (`typed`), as in Excel. */
  | { kind: "edit"; typed?: string }
  /** Delete or Backspace: the cell emptied — saved at once, no "Are you sure?"; a cell that must have a value refuses. */
  | { kind: "clear" };

/** Null: not the grid's key (Ctrl+C, a shortcut): the browser's. */
export function cellKey(e: CellKeyEvent): CellKey | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const move = (rows: number, cols: number, wrap = false): CellKey => ({ kind: "move", rows, cols, wrap });
  switch (e.key) {
    case "ArrowDown":
      return move(1, 0);
    case "ArrowUp":
      return move(-1, 0);
    case "ArrowRight":
      return move(0, 1);
    case "ArrowLeft":
      return move(0, -1);
    case "Tab":
      return move(0, e.shiftKey ? -1 : 1, true);
    case "Home":
      return { kind: "edge", last: false };
    case "End":
      return { kind: "edge", last: true };
    case "Enter":
    case "F2":
      return { kind: "edit" };
    case "Delete":
    case "Backspace":
      return { kind: "clear" };
  }
  return e.key.length === 1 ? { kind: "edit", typed: e.key } : null;
}

/**
 * The cell a move lands on, in a grid of `rowCount` rows by `colCount` columns: clamped to the grid;
 * a `wrap` move past a row's end goes on in the next (previous) row.
 */
export function cellAfter(
  at: { row: number; col: number },
  key: Extract<CellKey, { kind: "move" | "edge" }>,
  rowCount: number,
  colCount: number,
): { row: number; col: number } {
  const clamp = (n: number, max: number) => Math.max(0, Math.min(max - 1, n));
  if (key.kind === "edge") return { row: at.row, col: key.last ? colCount - 1 : 0 };
  let row = at.row + key.rows;
  let col = at.col + key.cols;
  if (key.wrap && col >= colCount && row + 1 < rowCount) [row, col] = [row + 1, 0];
  else if (key.wrap && col < 0 && row > 0) [row, col] = [row - 1, colCount - 1];
  return { row: clamp(row, rowCount), col: clamp(col, colCount) };
}

/** The Worklist's fill-down keys, read before `cellKey` (the Shipping schedule has none). */
export type RangeKey =
  /** Shift+↓ / Shift+↑: the range of cells down (up) the cursor's column grows or shrinks by a row. */
  | { kind: "extend"; rows: number }
  /** Ctrl+D (Cmd+D): fill down — the range from its top cell, or the cell from the one above it. */
  | { kind: "fill" }
  /** Esc: the range let go. */
  | { kind: "clear" };

export function rangeKey(e: CellKeyEvent): RangeKey | null {
  if (e.altKey) return null;
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "d") return { kind: "fill" };
  if (e.ctrlKey || e.metaKey) return null;
  if (e.shiftKey && e.key === "ArrowDown") return { kind: "extend", rows: 1 };
  if (e.shiftKey && e.key === "ArrowUp") return { kind: "extend", rows: -1 };
  if (e.key === "Escape") return { kind: "clear" };
  return null;
}
